import {
  CURRENCY_TAB_ID,
  INITIAL_SELECTIONS,
  PREFERENCE_CONFIGS,
  SHIPMENT_COUNTRY_TAB_ID,
  SHIPPING_TIME_TAB_ID,
  SUPPLIERS_TAB_ID,
  TAB_CONFIGS,
  normalizeText,
} from '@Components/CommercialSubstancesPreferences/CommercialSubstancesPreferencesConstants';
import { stripExceptionTags } from '@Services/HtmlSanitizeService';
import {
  AGGREGATOR_PREVIEW_LIMIT,
  COMMERCIAL_SUPPLIER_KEY_DELIMITER,
  COMMERCIAL_SUPPLIER_SORT_DIRECTIONS,
  COMMERCIAL_SUPPLIERS_SELECTED_TABLE_COLUMN,
  COMMERCIAL_SUPPLIERS_TABLE_COLUMNS,
  DEFAULT_DETAIL_SORT_STATE,
  OUTER_TABLE_SORTABLE_COLUMNS,
} from './constants';

const MASS_UNIT_MULTIPLIERS = {
  mcg: 0.001,
  ug: 0.001,
  μg: 0.001,
  mg: 1,
  g: 1000,
  kg: 1000000,
};

const VOLUME_UNIT_MULTIPLIERS = {
  ul: 0.001,
  μl: 0.001,
  ml: 1,
  l: 1000,
};

function joinKeyParts(keyParts = []) {
  return keyParts.join(COMMERCIAL_SUPPLIER_KEY_DELIMITER);
}

function cleanHighlightedText(value) {
  if (value === undefined || value === null) {
    return '';
  }

  return stripExceptionTags(`${value}`).trim();
}

function cloneTableColumns(columns = []) {
  return columns.map((column) => ({ ...column }));
}

function hasCommercialSupplierSummaryColumnValue(group = {}, columnValue = '') {
  const summaryOffer = group?.summaryOffer || {};

  if (columnValue === 'supplier') return true;
  if (columnValue === 'purity') return Boolean(summaryOffer.purityValue || summaryOffer.purityText);
  if (columnValue === 'size') return Boolean(summaryOffer.packageSize);
  if (columnValue === 'price') return Boolean(summaryOffer.priceValue || summaryOffer.priceText);

  return true;
}

export function getCommercialSupplierTableColumns(isProdSelectionEnabled = false, groups = []) {
  const allColumns = cloneTableColumns(COMMERCIAL_SUPPLIERS_TABLE_COLUMNS);
  const visibleColumns = allColumns.filter(
    (column) =>
      column.value === 'supplier' ||
      groups.length === 0 ||
      groups.some((group) => hasCommercialSupplierSummaryColumnValue(group, column.value))
  );

  if (!isProdSelectionEnabled) return visibleColumns;

  return [{ ...COMMERCIAL_SUPPLIERS_SELECTED_TABLE_COLUMN }, ...visibleColumns];
}

export function getNextDetailSortState(previousSortState, field) {
  const resolvedPreviousSortState = previousSortState || DEFAULT_DETAIL_SORT_STATE;

  if (resolvedPreviousSortState.field !== field || resolvedPreviousSortState.direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.neutral) {
    return {
      field,
      direction: COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc,
    };
  }

  if (resolvedPreviousSortState.direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc) {
    return {
      field,
      direction: COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc,
    };
  }

  return DEFAULT_DETAIL_SORT_STATE;
}

export function getCommercialSupplierRowKey(row = {}, rowIndex = 0) {
  if (row.index !== undefined && row.index !== null && row.index !== '') {
    return `index:${row.index}`;
  }

  return joinKeyParts([
    row.rcSupplierId || row.supplierName || `supplier-${rowIndex}`,
    row.rcOriginalSupplierId || row.originalSupplierName || '',
    row.rcSubstanceId || '',
    row.catalogProductNumber || row.suppliersProductNumber || row.suppliersSKU || '',
    row.packageSize || '',
    cleanHighlightedText(row.priceValue || row.priceText),
  ]);
}

export function dedupeCommercialSupplierRows(rows = []) {
  const seenRows = new Set();

  return rows.filter((row, rowIndex) => {
    const rowKey = getCommercialSupplierRowKey(row, rowIndex);
    if (seenRows.has(rowKey)) return false;

    seenRows.add(rowKey);
    return true;
  });
}

export function getOfferIdentifier(offer = {}) {
  return offer.catalogProductNumber || offer.suppliersProductNumber || offer.suppliersSKU || '';
}

function getPriceCurrencyValue(offer = {}) {
  if (offer.priceCurrency) {
    const priceCurrency = cleanHighlightedText(offer.priceCurrency);
    return priceCurrency.toUpperCase();
  }

  const priceText = cleanHighlightedText(offer.priceText);
  const priceCurrencyMatch = priceText.match(/^[\d,.]+\s*([a-zA-Z]{3})\b/);
  const priceCurrency = priceCurrencyMatch?.[1] || '';

  return priceCurrency.toUpperCase();
}

function getPreferenceFilterValue(filter, path, fallbackValue) {
  if (!filter) {
    return fallbackValue;
  }

  if (filter.getIn) {
    return filter.getIn(path, fallbackValue);
  }

  return path.reduce((value, pathPart) => value?.[pathPart], filter) ?? fallbackValue;
}

function getPreferenceFilterProperty(filter) {
  const property = getPreferenceFilterValue(filter, ['property'], '');

  if (!property) {
    return property;
  }

  return property?.property || property?.get?.('property') || property;
}

function getPreferenceFilterFacetValues(filter) {
  const facetValues = getPreferenceFilterValue(filter, ['_facetsForTracking', 'values'], []) || [];

  return facetValues.toArray ? facetValues.toArray() : facetValues;
}

function isExplicitlyAppliedPreferenceFilter(filter, tabId) {
  const preferenceConfig = PREFERENCE_CONFIGS[tabId];
  const facets = getPreferenceFilterValue(filter, ['_facetsForTracking'], {});
  const isPreference = facets?.isPreference || facets?.get?.('isPreference');

  return Boolean(isPreference) && normalizeText(getPreferenceFilterProperty(filter)) === normalizeText(preferenceConfig.resultFilterId);
}

function getBreadcrumbFilters(activeBreadcrumb) {
  const breadcrumbFilters = getPreferenceFilterValue(activeBreadcrumb, ['filters'], []);

  if (!breadcrumbFilters) {
    return [];
  }

  return breadcrumbFilters.toArray ? breadcrumbFilters.toArray() : breadcrumbFilters;
}

export function getCommercialSubstancePreferencesFromBreadcrumb(activeBreadcrumb) {
  const breadcrumbFilters = getBreadcrumbFilters(activeBreadcrumb);

  return TAB_CONFIGS.reduce(
    (preferences, tab) => {
      const preferenceFilter = breadcrumbFilters.find(
        (filter) =>
          normalizeText(getPreferenceFilterProperty(filter)) === normalizeText(PREFERENCE_CONFIGS[tab.id].filterId) ||
          isExplicitlyAppliedPreferenceFilter(filter, tab.id)
      );

      return {
        ...preferences,
        [tab.id]: getPreferenceFilterFacetValues(preferenceFilter),
      };
    },
    { ...INITIAL_SELECTIONS }
  );
}

function hasMatchingPreference(selectedValues = [], rowValues = [], { allowPartialMatch = false } = {}) {
  if (!selectedValues.length) {
    return true;
  }

  const normalizePreferenceValue = (value) => normalizeText(value).replace(/[^a-z0-9]+/g, '');
  const normalizedSelectedValues = selectedValues.map(normalizePreferenceValue).filter(Boolean);

  return rowValues.some((rowValue) => {
    const normalizedRowValue = normalizePreferenceValue(rowValue);

    return normalizedSelectedValues.some(
      (selectedValue) =>
        normalizedRowValue === selectedValue ||
        (allowPartialMatch &&
          selectedValue.length >= 4 &&
          normalizedRowValue.length >= 4 &&
          (normalizedRowValue.startsWith(selectedValue) || selectedValue.startsWith(normalizedRowValue)))
    );
  });
}

export function filterCommercialSupplierRowsByPreferences(rows = [], preferences = {}) {
  const selectedSuppliers = preferences[SUPPLIERS_TAB_ID] || [];
  const selectedCurrencies = preferences[CURRENCY_TAB_ID] || [];
  const selectedShipmentCountries = preferences[SHIPMENT_COUNTRY_TAB_ID] || [];
  const selectedShippingTimes = preferences[SHIPPING_TIME_TAB_ID] || [];

  return rows.filter(
    (row) =>
      hasMatchingPreference(selectedSuppliers, [row.supplierName, row.originalSupplierName], { allowPartialMatch: true }) &&
      hasMatchingPreference(selectedCurrencies, [getPriceCurrencyValue(row)]) &&
      hasMatchingPreference(selectedShipmentCountries, [row.productLocation, row.supplierLocation]) &&
      hasMatchingPreference(selectedShippingTimes, [row.shipmentTier, row.shipmentTime])
  );
}

export function hasSinglePriceCurrency(offers = []) {
  const currencies = new Set();

  offers.forEach((offer) => {
    if (!offer?.priceValue && !offer?.priceText) return;

    const priceCurrency = getPriceCurrencyValue(offer);
    if (priceCurrency) {
      currencies.add(priceCurrency);
    }
  });

  return currencies.size === 1;
}

export function getCommercialSupplierTableSortableColumns(columns = COMMERCIAL_SUPPLIERS_TABLE_COLUMNS, groups = []) {
  const visibleColumnValues = new Set();

  columns.forEach((column) => {
    visibleColumnValues.add(column.value);
  });

  const sortableColumns = {};

  if (visibleColumnValues.has('purity') && OUTER_TABLE_SORTABLE_COLUMNS.purity) {
    sortableColumns.purity = true;
  }

  if (visibleColumnValues.has('size') && OUTER_TABLE_SORTABLE_COLUMNS.size) {
    sortableColumns.size = true;
  }

  if (
    visibleColumnValues.has('price') &&
    OUTER_TABLE_SORTABLE_COLUMNS.price &&
    hasSinglePriceCurrency(groups.map((group) => group?.summaryOffer || {}))
  ) {
    sortableColumns.price = true;
  }

  return sortableColumns;
}

export function formatPurityValue(offer = {}) {
  if (offer.purityValue) return `${offer.purityValue}%`;
  return offer.purityText || '';
}

export function formatPriceValue(offer = {}) {
  if (offer.priceValue && offer.priceCurrency) {
    return `${cleanHighlightedText(offer.priceValue)} ${cleanHighlightedText(offer.priceCurrency)}`;
  }

  return cleanHighlightedText(offer.priceText);
}

function keepHighlightedText(value) {
  if (value === undefined || value === null) {
    return '';
  }

  return `${value}`.trim();
}

export function formatPriceValueHtml(offer = {}) {
  if (offer.priceValue && offer.priceCurrency) {
    return `${keepHighlightedText(offer.priceValue)} ${keepHighlightedText(offer.priceCurrency)}`;
  }

  return keepHighlightedText(offer.priceText);
}

export function formatShippingValue(offer = {}) {
  return offer.shipmentTier || offer.shipmentTime || '';
}

export function formatAvailabilityValue(offer = {}) {
  if (!offer.stockStatus) return '';
  return offer.stockStatus === 'true' ? 'Available' : offer.stockStatus;
}

export function formatShipmentCountryValue(offer = {}) {
  return offer.productLocation || '';
}

export function parsePackageSizeValue(packageSize) {
  if (!packageSize) return null;

  const packageSizeString = `${packageSize}`;
  const trimmedPackageSize = packageSizeString.trim();
  const match = trimmedPackageSize.match(/^([\d,.]+)\s*([a-zA-Z\u00B5]+)?/);

  if (!match) return null;

  const numericPart = match?.[1];
  const unitPart = match?.[2];
  const numericValue = Number((numericPart || '').replace(/,/g, ''));
  if (Number.isNaN(numericValue)) return null;

  const rawUnit = unitPart ? unitPart.toLowerCase() : null;

  if (!rawUnit) return numericValue;
  if (MASS_UNIT_MULTIPLIERS[rawUnit]) return numericValue * MASS_UNIT_MULTIPLIERS[rawUnit];
  if (VOLUME_UNIT_MULTIPLIERS[rawUnit]) return numericValue * VOLUME_UNIT_MULTIPLIERS[rawUnit];

  return numericValue;
}

function getSizeSortValue(offer = {}) {
  const parsedPackageSizeValue = parsePackageSizeValue(offer.packageSize);
  if (parsedPackageSizeValue !== null) return parsedPackageSizeValue;

  if (offer.packageSizeNormalized !== undefined && offer.packageSizeNormalized !== null && offer.packageSizeNormalized !== '') {
    const normalizedValue = Number(offer.packageSizeNormalized);
    if (!Number.isNaN(normalizedValue)) return normalizedValue;
  }

  return null;
}

function getPuritySortValue(offer = {}) {
  const purityCandidates = [offer.purityValue, offer.purityText];
  const parsedPurityValue = purityCandidates.reduce((resolvedValue, purityCandidate) => {
    if (resolvedValue !== null) return resolvedValue;
    if (purityCandidate === undefined || purityCandidate === null || purityCandidate === '') return null;

    const directPurityValue = Number(purityCandidate);
    if (!Number.isNaN(directPurityValue)) return directPurityValue;

    const purityText = `${purityCandidate}`.trim();
    const purityMatch = purityText.match(/(\d+(?:[.,]\d+)?)/);
    const purityMatchValue = purityMatch?.[1];
    if (!purityMatchValue) return null;

    const numericPurityValue = Number(purityMatchValue?.replace(/,/g, ''));
    return Number.isNaN(numericPurityValue) ? null : numericPurityValue;
  }, null);

  return parsedPurityValue;
}

function getPriceSortValue(offer = {}) {
  const directPriceText = cleanHighlightedText(offer.priceValue);
  const directPriceValue = directPriceText === '' ? Number.NaN : Number(directPriceText);
  if (!Number.isNaN(directPriceValue)) return directPriceValue;

  const priceText = cleanHighlightedText(offer.priceText);
  const priceMatch = priceText.match(/^([\d,.]+)/);
  if (!priceMatch?.[1]) return null;

  const parsedPriceValue = Number(priceMatch?.[1]?.replace(/,/g, ''));
  if (!Number.isNaN(parsedPriceValue)) return parsedPriceValue;

  return null;
}

function compareNullableNumbers(leftValue, rightValue, direction = COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc) {
  if (leftValue === null && rightValue === null) return 0;
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;

  return direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc ? leftValue - rightValue : rightValue - leftValue;
}

function compareStrings(leftValue, rightValue) {
  const leftString = String(leftValue || '');
  const rightString = String(rightValue || '');

  return leftString.localeCompare(rightString);
}

function compareDatesDescending(leftValue, rightValue) {
  const leftTimestamp = leftValue ? Date.parse(leftValue) : Number.NEGATIVE_INFINITY;
  const rightTimestamp = rightValue ? Date.parse(rightValue) : Number.NEGATIVE_INFINITY;

  return rightTimestamp - leftTimestamp;
}

export function sortOffersByPackageSize(offers = []) {
  return [...offers].sort((leftOffer, rightOffer) => {
    const sizeCompare = compareNullableNumbers(
      getSizeSortValue(leftOffer),
      getSizeSortValue(rightOffer),
      COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc
    );
    if (sizeCompare !== 0) return sizeCompare;

    const priceCompare = compareNullableNumbers(
      getPriceSortValue(leftOffer),
      getPriceSortValue(rightOffer),
      COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc
    );
    if (priceCompare !== 0) return priceCompare;

    return compareStrings(leftOffer.index, rightOffer.index);
  });
}

export function sortOffersByDetailSortState(offers = [], sortState = DEFAULT_DETAIL_SORT_STATE) {
  if (!sortState?.field || sortState.direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.neutral) return sortOffersByPackageSize(offers);

  const normalizedSortState = sortState;
  const direction =
    normalizedSortState.direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc
      ? COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc
      : COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc;

  return [...offers].sort((leftOffer, rightOffer) => {
    if (normalizedSortState.field === 'price') {
      const priceCompare = compareNullableNumbers(getPriceSortValue(leftOffer), getPriceSortValue(rightOffer), direction);
      if (priceCompare !== 0) return priceCompare;

      const sizeCompare = compareNullableNumbers(
        getSizeSortValue(leftOffer),
        getSizeSortValue(rightOffer),
        COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc
      );
      if (sizeCompare !== 0) return sizeCompare;
    }

    const sizeCompare = compareNullableNumbers(getSizeSortValue(leftOffer), getSizeSortValue(rightOffer), direction);
    if (sizeCompare !== 0) return sizeCompare;

    const purityCompare = compareNullableNumbers(
      getPuritySortValue(leftOffer),
      getPuritySortValue(rightOffer),
      COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc
    );
    if (purityCompare !== 0) return purityCompare;

    const priceCompare = compareNullableNumbers(
      Number(leftOffer.priceValue),
      Number(rightOffer.priceValue),
      COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc
    );
    if (priceCompare !== 0) return priceCompare;

    return compareStrings(leftOffer.index, rightOffer.index);
  });
}

export function sortAggregatorRows(offers = []) {
  return [...offers].sort((leftOffer, rightOffer) => {
    const supplierCompare = compareStrings(
      leftOffer.originalSupplierName || leftOffer.supplierName,
      rightOffer.originalSupplierName || rightOffer.supplierName
    );
    if (supplierCompare !== 0) return supplierCompare;

    const identifierCompare = compareStrings(getOfferIdentifier(leftOffer), getOfferIdentifier(rightOffer));
    if (identifierCompare !== 0) return identifierCompare;

    return compareStrings(leftOffer.rcProductName, rightOffer.rcProductName);
  });
}

function getDetailSortValue(offer = {}, sortState = DEFAULT_DETAIL_SORT_STATE) {
  if (sortState?.field === 'price') return getPriceSortValue(offer);
  return getSizeSortValue(offer);
}

function isActiveDetailSortState(sortState = DEFAULT_DETAIL_SORT_STATE) {
  return Boolean(sortState?.field && sortState.direction !== COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.neutral);
}

function sortAggregatorSupplierGroupsByDetailSortState(supplierGroups = [], sortState = DEFAULT_DETAIL_SORT_STATE) {
  const direction =
    sortState?.direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc
      ? COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc
      : COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc;
  const supplierGroupsWithSortMeta = supplierGroups.map((supplierGroup) => {
    const offers = sortOffersByDetailSortState(supplierGroup.offers, sortState);
    const primaryOffer = offers?.[0] || {};

    return {
      ...supplierGroup,
      offers,
      primaryIdentifier: getOfferIdentifier(primaryOffer),
      sortValue: getDetailSortValue(primaryOffer, sortState),
    };
  });

  const sortedSupplierGroups = isActiveDetailSortState(sortState)
    ? supplierGroupsWithSortMeta.sort((leftGroup, rightGroup) => {
        const sortValueCompare = compareNullableNumbers(leftGroup.sortValue, rightGroup.sortValue, direction);
        if (sortValueCompare !== 0) return sortValueCompare;

        const supplierCompare = compareStrings(leftGroup.supplierName, rightGroup.supplierName);
        if (supplierCompare !== 0) return supplierCompare;

        return compareStrings(leftGroup.primaryIdentifier, rightGroup.primaryIdentifier);
      })
    : supplierGroupsWithSortMeta;

  return sortedSupplierGroups.map(({ primaryIdentifier: _primaryIdentifier, sortValue: _sortValue, ...supplierGroup }) => supplierGroup);
}

function getAggregatorSupplierGroupKey(offer = {}) {
  return offer.originalSupplierName || offer.supplierName || getOfferIdentifier(offer) || offer.rcSubstanceId;
}

function applyRowSpanMeta(rows = [], fieldNames = []) {
  const rowsWithMeta = rows.map((row) => ({ ...row }));

  fieldNames.forEach((fieldName) => {
    let rowIndex = 0;

    while (rowIndex < rowsWithMeta.length) {
      let groupEndIndex = rowIndex + 1;
      const currentRow = rowsWithMeta?.[rowIndex];
      const currentValue = currentRow?.[fieldName];

      while (groupEndIndex < rowsWithMeta.length && rowsWithMeta?.[groupEndIndex]?.[fieldName] === currentValue) {
        groupEndIndex += 1;
      }

      if (currentRow) {
        currentRow[`${fieldName}RowSpan`] = groupEndIndex - rowIndex;
      }

      for (let hiddenIndex = rowIndex + 1; hiddenIndex < groupEndIndex; hiddenIndex += 1) {
        const hiddenRow = rowsWithMeta?.[hiddenIndex];
        if (hiddenRow) {
          hiddenRow[`${fieldName}Hidden`] = true;
        }
      }

      rowIndex = groupEndIndex;
    }
  });

  return rowsWithMeta;
}

function getDetailSubgroupKey(offer = {}) {
  return joinKeyParts([
    formatPurityValue(offer),
    formatShipmentCountryValue(offer),
    formatShippingValue(offer),
    formatAvailabilityValue(offer),
  ]);
}

function groupDetailOffersBySubgroup(getRowKey, offers = []) {
  const subgroupBlocks = new Map();

  offers.forEach((offer) => {
    const subgroupKey = getDetailSubgroupKey(offer);
    let subgroupBlock = subgroupBlocks.get(subgroupKey);

    if (!subgroupBlock) {
      subgroupBlock = {
        key: getRowKey(subgroupKey, offer, subgroupBlocks.size),
        subgroupKey,
        purityValue: formatPurityValue(offer),
        shipmentCountryValue: formatShipmentCountryValue(offer),
        shippingValue: formatShippingValue(offer),
        availabilityValue: formatAvailabilityValue(offer),
        offers: [],
      };
      subgroupBlocks.set(subgroupKey, subgroupBlock);
    }

    subgroupBlock.offers.push(offer);
  });

  return Array.from(subgroupBlocks.values());
}

export function buildAggregatorPreviewRows(offers = [], supplierLimit = AGGREGATOR_PREVIEW_LIMIT, sortState = DEFAULT_DETAIL_SORT_STATE) {
  const supplierGroups = new Map();
  const sortedAggregatorOffers = sortAggregatorRows(offers);

  sortedAggregatorOffers.forEach((offer) => {
    const supplierKey = getAggregatorSupplierGroupKey(offer);

    if (!supplierGroups.has(supplierKey)) {
      supplierGroups.set(supplierKey, {
        supplierName: offer.originalSupplierName || offer.supplierName || 'Unknown supplier',
        offers: [],
      });
    }

    supplierGroups.get(supplierKey)?.offers?.push(offer);
  });

  const groupedSupplierValues = Array.from(supplierGroups.values());
  const sortedSupplierGroups = sortAggregatorSupplierGroupsByDetailSortState(groupedSupplierValues, sortState);
  const previewSupplierGroups = typeof supplierLimit === 'number' ? sortedSupplierGroups.slice(0, supplierLimit) : sortedSupplierGroups;

  return previewSupplierGroups.reduce((previewRows, supplierGroup) => {
    const subgroupBlocks = groupDetailOffersBySubgroup(
      (subgroupKey, offer, subgroupIndex) => joinKeyParts([supplierGroup.supplierName, subgroupKey, subgroupIndex]),
      supplierGroup.offers
    ).map((subgroupBlock) => ({
      ...subgroupBlock,
      supplierName: supplierGroup.supplierName,
    }));

    const groupedSubgroupBlocks = applyRowSpanMeta(subgroupBlocks, ['supplierName', 'purityValue']);
    const supplierPreviewRows = groupedSubgroupBlocks?.map((row, rowIndex, rows) => ({
      ...row,
      isSubgroupEnd: rowIndex === rows.length - 1,
    }));

    return previewRows.concat(supplierPreviewRows);
  }, []);
}

export function buildNormalPreviewRows(offers, offerLimit = null, sortState = DEFAULT_DETAIL_SORT_STATE) {
  const sortedOffers = sortOffersByDetailSortState(offers || [], sortState);
  const previewOffers = typeof offerLimit === 'number' ? sortedOffers.slice(0, offerLimit) : sortedOffers;

  return groupDetailOffersBySubgroup(
    (subgroupKey, offer, subgroupIndex) => joinKeyParts([subgroupKey, subgroupIndex, offer.rcSubstanceId || getOfferIdentifier(offer)]),
    previewOffers
  );
}

export function buildProductInformationRows(group = {}) {
  const rows = group.isAggregator ? group.aggregatorRows || [] : group.offers || [];
  const uniqueRows = new Map();

  rows.forEach((row) => {
    const rowKey = joinKeyParts([row.originalSupplierName || row.supplierName || '', getOfferIdentifier(row), row.rcProductName || '']);

    if (!uniqueRows.has(rowKey)) {
      uniqueRows.set(rowKey, {
        supplierName: row.originalSupplierName || row.supplierName || '',
        identificationNumber: getOfferIdentifier(row) || '',
        productName: row.rcProductName || '',
      });
    }
  });

  return Array.from(uniqueRows.values());
}

export function groupCommercialSupplierRows(rows = []) {
  const groupedRows = new Map();
  const uniqueRows = dedupeCommercialSupplierRows(rows);

  uniqueRows.forEach((row, rowIndex) => {
    const supplierId = row.rcSupplierId || row.supplierName || `unknown-supplier-${rowIndex}`;

    if (!groupedRows.has(supplierId)) {
      groupedRows.set(supplierId, {
        supplierId,
        supplierName: row.supplierName || 'Unknown supplier',
        supplierUrl: row.supplierUrl,
        supplierLocation: row.supplierLocation,
        lastUpdated: row.lastUpdated,
        offers: [],
        isAggregator: false,
      });
    }

    const supplierGroup = groupedRows.get(supplierId);
    const supplierOffers = supplierGroup?.offers || [];

    supplierOffers.push(row);
    supplierGroup.offers = supplierOffers;
    supplierGroup.isAggregator = supplierGroup.isAggregator || Boolean(row.rcOriginalSupplierId);

    if (!supplierGroup.supplierUrl && row.supplierUrl) supplierGroup.supplierUrl = row.supplierUrl;
    if (!supplierGroup.supplierLocation && row.supplierLocation) supplierGroup.supplierLocation = row.supplierLocation;

    if (row.lastUpdated && (!supplierGroup.lastUpdated || compareDatesDescending(supplierGroup.lastUpdated, row.lastUpdated) > 0)) {
      supplierGroup.lastUpdated = row.lastUpdated;
    }
  });

  const groupedValues = Array.from(groupedRows.values());

  return groupedValues.map((supplierGroup) => {
    const offers = sortOffersByPackageSize(supplierGroup.offers);
    const aggregatorRows = sortAggregatorRows(supplierGroup.offers);
    const aggregatorSupplierCount = new Set(aggregatorRows.map((offer) => getAggregatorSupplierGroupKey(offer))).size;

    return {
      ...supplierGroup,
      offers,
      summaryOffer: offers[0] || {},
      hiddenOfferCount: Math.max(offers.length - 1, 0),
      aggregatorRows,
      aggregatorSupplierCount,
    };
  });
}

export function buildCommercialSupplierTableData(groups = []) {
  return groups.map((group) => ({
    ...group,
    supplier: group.supplierName || '',
    purity: getPuritySortValue(group.summaryOffer),
    size: getSizeSortValue(group.summaryOffer),
    price: getPriceSortValue(group.summaryOffer),
  }));
}

export function buildCommercialSupplierTableDataKey(tableData = []) {
  return JSON.stringify(tableData.map((group) => [group.supplierId, group.offers?.length || 0]));
}

export function compareCommercialSupplierTableSortValues(leftValue, rightValue, direction = COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.asc) {
  const leftIsEmpty = leftValue === null || leftValue === undefined || leftValue === '';
  const rightIsEmpty = rightValue === null || rightValue === undefined || rightValue === '';

  if (leftIsEmpty && rightIsEmpty) return 0;
  if (leftIsEmpty) return 1;
  if (rightIsEmpty) return -1;

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc ? rightValue - leftValue : leftValue - rightValue;
  }

  const stringCompare = compareStrings(leftValue, rightValue);
  return direction === COMMERCIAL_SUPPLIER_SORT_DIRECTIONS.desc ? -stringCompare : stringCompare;
}

export function getExpandedDetailColumns(offers = []) {
  return {
    showPurity: offers.some((offer) => Boolean(formatPurityValue(offer))),
    showPackageSize: offers.some((offer) => Boolean(offer.packageSize)),
    showPrice: offers.some((offer) => Boolean(formatPriceValueHtml(offer))),
    showShipping: offers.some((offer) => Boolean(formatShippingValue(offer))),
    showShipmentCountry: offers.some((offer) => Boolean(formatShipmentCountryValue(offer))),
    showAvailability: offers.some((offer) => Boolean(formatAvailabilityValue(offer))),
  };
}
